import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useLazyLoading from "../../hooks/useLazyLoading";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";

//CSS
import "./messageView.css";
import "../../Pages/0PageStyles.css";

//Components
import Title from "../Other Components/Title";
import SearchBar from "../Other Components/searchBar";
import MessageCard from "../Message Components/messageCard";
import SortComponent from "../Other Components/sortComponent";
import Filter from "../Other Components/filter";
import { SelectionCategory } from "../Other Components/Category";
import PageLoader from "../Other Components/pageLoader";
import PopupConfirmation from "../Other Components/popupConfirmation";
import IconButton from "../Other Components/IconButton";
import Button from "../Other Components/button";

//Functions and hooks
import { GetMessages, DeleteMessages } from "../../BackendAccess/MessageHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import { AcceptEmployeeRequest } from "../../BackendAccess/EmployeeHooks";
import { compareDateTimes } from "../../BackendAccess/FrontendHelpers";
import { useTranslation } from "react-i18next";
import { usePopup } from "../../Contexts/PopupContext";
import { CheckForPermission } from "BackendAccess/UserHooks";

export default function MessageView({
  Tit = "Messages",
  bEnableSort = true,
  bEnableFilter = true,
  bEnableSearch = true,

  bEnableEdit = true,
  bShowTitle = true,

  bSelectionView = false,
  onselectedMessagesChange,

  bDisabled = false,
  disabledMsg = "Select a warehouse and client first.",
}) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);

  const [categoryArray, setCategoryArray] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchFilteredMessages, setSearchFilteredMessages] = useState([]);

  const [selectAllChecked, setSelectAllChecked] = useState(false);

  const [canSendAnnouncement, setCanSendAnnouncement] = useState(false);

  //Variables for selection and countable modes
  const [selectedMessages, setSelectedMessages] = useState([]);

  //stuff for lazy loading
  const lazyLoadMessageLoadCount = 20;
  const lazyLoadPixelThreshold = 200;

  const isNarrow = useNarrowMode();
  const { t } = useTranslation();
  const { showPopup, removePopup } = usePopup();

  const showConfirmationPopup = () => {
    const key = Date.now();
    showPopup(
      <PopupConfirmation
        style={"warning"}
        message={
          <p>
            {t("Are you sure you want to delete these messages?", {
              ns: "messages",
            })}
            <br />{" "}
            {t(
              "This action will delete all of the selected messages from your inbox!",
              { ns: "messages" }
            )}
            <br />
            <span className="text-negative">
              {t("This action cannot be undone.")}
            </span>
          </p>
        }
        onConfirm={() => handleConfirmDelete(key)}
        onCancel={() => handleCancelDelete(key)}
      />,
      key
    );
  };

  ////////////////////////////////////Effects/////////////////////////////////////////////

  const fetchDataFromServer = async () => {
    setLoading(true);

    try {
      await fetchMessages();
      await fetchAnnouncementPerm();
    } catch (error) {
      error.isPageError = true;
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    const [data, error] = await fetchWithTimeout(GetMessages);

    if (error) {
      throw error;
    } else {
      setMessages(data);
    }
  };

  useEffect(() => {
    fetchDataFromServer();
  }, []);

  const fetchAnnouncementPerm = async () => {
    // A "no" here just means the button stays hidden - it's not something
    // worth surfacing to the user as a page error (this fires for every
    // role, including Guests, who are always denied by design).
    try {
      const [data, error] = await CheckForPermission("SendAnnouncement");
      setCanSendAnnouncement(!error && !!data);
    } catch (err) {
      setCanSendAnnouncement(false);
    }
  };

  useEffect(() => {
    if (
      onselectedMessagesChange !== undefined &&
      typeof onselectedMessagesChange === "function"
    ) {
      onselectedMessagesChange(selectedMessages);
    }
    if (selectedMessages.length > 0) {
      setSelectAllChecked(true);
    } else {
      setSelectAllChecked(false);
    }
  }, [selectedMessages]);

  useEffect(() => {
    if (messages.length > 0) {
      createCategoryArray();
    }
  }, [messages]);

  useEffect(() => {
    if (messages.length > 0) setFilteredMessages(messages);
  }, [messages]);

  ////////////////////////////////////Effects end/////////////////////////////////////////////

  ////////////////////////////////////Filtering////////////////////////////////////////////////

  const createCategoryArray = () => {
    const newCatArr = [
      new SelectionCategory(
        "Type",
        [...new Set(messages.map((message) => message.Type))].map((type) => ({
          ItemName: type,
          selected: false,
        })),
        false,
        MessageHasOptionType
      ),
      new SelectionCategory(
        "Sender",
        [...new Set(messages.map((message) => message.SenderName))].map(
          (name) => ({
            ItemName: name,
            selected: false,
          })
        ),
        false,
        MessageHasOptionSender
      ),
    ];
    setCategoryArray(newCatArr);
  };

  const MessageHasOptionType = (message, type) => {
    return message.Type === type;
  };
  const MessageHasOptionSender = (message, senderName) => {
    return message.SenderName === senderName;
  };

  const setNewCategoryArray = (newCategoryArray) => {
    setCategoryArray(newCategoryArray);
  };

  const setNewMessages = (newMessages) => {
    setFilteredMessages(newMessages);
  };
  ////////////////////////////////////Filtering end////////////////////////////////////////////////

  /////////////////////////////////SEARCHING AND SORTING/////////////////////////////////////////
  // Handle search input
  const handleSearchChange = (term) => {
    setSearchTerm(term);
  };

  // Filter messages based on search term
  useEffect(() => {
    const newSearchFilteredMessages = filteredMessages.filter((message) => {
      const lowerCaseTerm = searchTerm.toLowerCase();
      return (
        message.Header.toLowerCase().includes(lowerCaseTerm) ||
        message.Body.toLowerCase().includes(lowerCaseTerm) ||
        message.SenderName.toLowerCase().includes(lowerCaseTerm)
      );
    });
    setSearchFilteredMessages(newSearchFilteredMessages);
  }, [searchTerm, filteredMessages]);

  const dateSortFunction = (direction) => {
    if (messages.length === 0) return;
    var messagesSorted = [...filteredMessages].sort(
      (a, b) => compareDateTimes(a.DateTime, b.DateTime) * direction
    );
    setMessages(messagesSorted);
  };
  const typeSortFunction = (direction) => {
    if (messages.length === 0) return;

    var messagesSorted = [...filteredMessages].sort((a, b) =>
      a.Type.toUpperCase() > b.Type.toUpperCase()
        ? direction * 1
        : -1 * direction
    );
    setMessages(messagesSorted);
  };
  const senderSortFunction = (direction) => {
    if (messages.length === 0) return;

    var messagesSorted = [...filteredMessages].sort((a, b) =>
      a.SenderName.toUpperCase() > b.SenderName.toUpperCase()
        ? direction * 1
        : -1 * direction
    );
    setMessages(messagesSorted);
  };

  ////////////////////////////////////////SEARCHING AND SORTING END//////////////////////////////////////

  ////////////////////////////////////////////////Lazy loading///////////////////////////////////////
  const { loadedItems, itemContainerRef } = useLazyLoading(
    searchFilteredMessages,
    lazyLoadMessageLoadCount,
    lazyLoadPixelThreshold
  );

  ////////////////////////////////////////////////Lazy loading end///////////////////////////////////////

  /////////////////////////////////////////Handle btn clicks////////////////////////////////////////
  const handleEditClick = async (message) => {
    navigate("/workspace/messages/messageViewPage", { state: { message } });
  };

  const handleConfirmDelete = async (key) => {
    showNotification(t("Deleting messages...", { ns: "messages" }), "info");
    try {
      const [deleteData, deleteError] = await DeleteMessages([
        selectedMessages,
      ]);
      if (deleteError) {
        setError(deleteError);
        return;
      }
      showNotification(
        t("Messages deleted successfully!", { ns: "messages" }),
        "success"
      );
      handleRefresh();
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      removePopup(key);
    }
  };

  const handleCancelDelete = (key) => {
    removePopup(key);
  };

  const onCardSelectLocal = (newState, MessageID) => {
    let newSelectedMessages = [...selectedMessages];
    if (newState) {
      newSelectedMessages.push(MessageID);
    } else {
      newSelectedMessages = newSelectedMessages.filter((item) => {
        return parseInt(item) !== parseInt(MessageID);
      });
    }
    setSelectedMessages(newSelectedMessages);
  };

  const onSelectAllClick = () => {
    if (selectAllChecked) {
      setSelectedMessages([]);
    } else {
      setSelectedMessages(messages.map((item) => item.MessageID));
    }

    setSelectAllChecked(!selectAllChecked);
  };

  const onDeleteSelectedClick = () => {
    showConfirmationPopup();
  };

  const handleRefresh = () => {
    window.location.reload();
  };
  const handleAcceptClick = async (MessageID) => {
    try {
      const [data, error] = await AcceptEmployeeRequest(MessageID);
      if (error) {
        setError(error);
      } else {
        showNotification(t("Invite accepted!", { ns: "messages" }), "success");
        handleRefresh();
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    }
  };
  const handleAnnouncementClick = async () => {
    navigate("/workspace/messages/announcementPage");
  };
  /////////////////////////////////////////Handle btn clicks end////////////////////////////////////////

  return (
    <div className="item-view-container">
      <PageLoader loading={loading} error={pageError}>
        {bDisabled ? (
          <div className="itemView disabledCover">
            <p className="itemView disabledMsg">{disabledMsg}</p>
          </div>
        ) : null}
        {bShowTitle ? <Title>{Tit}</Title> : null}
        {canSendAnnouncement && (
          <div className="btn-container employee-view">
            <Button
              icon="announcement"
              className={isNarrow ? "mt-2" : ""}
              btnStyle={"btn-positive"}
              onClick={handleAnnouncementClick}
            >
              {t("Send announcement")}
            </Button>
          </div>
        )}
        {bEnableSearch ? (
          <div className="search-bar-container">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder={t("Search messages...", { ns: "messages" })}
            />
          </div>
        ) : null}
        <div className="sortAndFilterBar">
          {bEnableSort ? (
            <SortComponent
              sortOpts={["Date", "Sender", "Type"]}
              sortOptFunctions={[
                dateSortFunction,
                typeSortFunction,
                senderSortFunction,
              ]}
            />
          ) : null}
          {bEnableFilter ? (
            <Filter
              categoryArray={categoryArray}
              ConstItems={messages}
              Messages={filteredMessages}
              setNewItems={setNewMessages}
              setNewCatArr={setNewCategoryArray}
            />
          ) : null}
        </div>
        <div className="selected-options-container">
          <div
            className="checkbox-container message-view"
            onClick={onSelectAllClick}
          >
            <div
              className={"checkbox " + (selectAllChecked ? "checked" : "")}
            ></div>
          </div>
          <IconButton icon="refresh" onClick={handleRefresh}></IconButton>
          {selectAllChecked ? (
            <IconButton
              icon="delete"
              onClick={onDeleteSelectedClick}
            ></IconButton>
          ) : null}
        </div>

        <div className="item-card-and-header-container">
          <div className="item-card-container" ref={itemContainerRef}>
            {loadedItems
              ? loadedItems.map((message, index) => (
                  <MessageCard
                    key={index}
                    MessageID={message.MessageID}
                    Header={message.Header}
                    Body={message.Body}
                    Type={message.Type}
                    Token={message.Token}
                    Sender={message.SenderName}
                    DateTime={message.DateTime}
                    bBtnVisible={bEnableEdit}
                    bSelectable={bSelectionView}
                    onClick={() => handleEditClick(message)}
                    onSelected={onCardSelectLocal}
                    selectedItemIds={selectedMessages}
                    bDisabled={loading}
                    onAcceptClick={handleAcceptClick}
                  />
                ))
              : null}
          </div>
        </div>
      </PageLoader>
    </div>
  );
}
